import Image from 'next/image';

import type { SanityImage } from '@app/typeStore';
import { cn } from '@app/ui/_lib/utils';
import type { HTMLAttributes } from 'react';
import { Hotspots, type HotspotData } from '../Hotspots';

export const imageObjectFitValues = ['cover', 'contain'] as const;

/**
 * ResponsiveImage Component
 *
 * Renders an optimized responsive image with support for hotspots, crops, and various
 * display options. Handles Sanity.io image assets with proper dimension calculations
 * and image transformations.
 *
 * @param props.className - Optional CSS class name for styling the container
 * @param props.image - The image object containing asset data and optional hotspot/crop info
 * @param props.objectFit - How the image should fit within its container ("cover" or "contain")
 * @param props.isHero - Whether this is a hero/priority image (enables eager loading and high fetch priority)
 * @param props.customResizeDimensions - Optional custom resize for scaling the image. Calculates image dimensions that ensure the smallest dimension fits the customResizeDimensions while maintaining aspect ratio.
 * @param props.sizes - Responsive size configurations for the image
 * @param props.quality - Image quality (1-100)
 * @param props.loading - Image loading behavior ("lazy" or "eager")
 * @param props.aspectRatio - Image aspect ratio
 *
 * @returns JSX.Element | null - Image component or null if no valid image is provided
 */
export type ResponsiveImageProps = {
	className?: string;
	image: SanityImage;
	objectFit?: (typeof imageObjectFitValues)[number];
	isHero?: boolean;
	customResizeDimensions?: {
		width?: number;
		height?: number;
	};
	sizes?: string;
	quality?: number;
	priority?: boolean;
	loading?: 'lazy' | 'eager';
	aspectRatio?: string;
	hotspots?: HotspotData[];
};

export function ResponsiveImage({
	className,
	image,
	objectFit = 'cover',
	isHero = false,
	priority = false,
	customResizeDimensions = {
		width: 2000,
	},
	sizes = '(max-width: 768px) 87vw, (max-width: 1440px) 50vw, 30vw',
	quality = 95,
	loading,
	aspectRatio,
	...props
}: HTMLAttributes<HTMLSpanElement> & ResponsiveImageProps) {
	if (objectFit === 'contain') {
		aspectRatio = undefined;
	}
	// Return null if no image asset
	if (!image?.asset) {
		return null;
	}

	const asset = processImageReference(image);
	if (!asset) {
		return null;
	}

	let imageUrl = '';
	let objectPosition = 'center'; // Default object position

	// Process hotspot if it exists
	if (image.hotspot) {
		// Validate hotspot coordinates are within expected range
		const x = typeof image.hotspot.x === 'number' && image.hotspot.x >= 0 && image.hotspot.x <= 1 ? image.hotspot.x : 0.5;
		const y = typeof image.hotspot.y === 'number' && image.hotspot.y >= 0 && image.hotspot.y <= 1 ? image.hotspot.y : 0.5;

		const hotspotX = x * 100;
		const hotspotY = y * 100;

		objectPosition = `${hotspotX}% ${hotspotY}%`;
	}

	let imageWidth = asset.metadata.dimensions?.width ? asset.metadata.dimensions.width : 0;
	let imageHeight = asset.metadata.dimensions?.height ? asset.metadata.dimensions.height : 0;

	imageUrl = asset.url;

	const crop = calculateAspectRatioCrop(image, aspectRatio || '', imageWidth, imageHeight);

	if (customResizeDimensions && (customResizeDimensions.width || customResizeDimensions.height)) {
		const customWidth =
			customResizeDimensions.width ??
			(customResizeDimensions.height ? customResizeDimensions.height * (crop.width / crop.height) : crop.width);
		const customHeight =
			customResizeDimensions.height ??
			(customResizeDimensions.width ? customResizeDimensions.width * (crop.height / crop.width) : crop.height);

		const customDimensions = {
			width: customWidth,
			height: customHeight,
		};
		const fitDimensions = calculateFitDimensions(
			{
				width: Number(crop.width),
				height: Number(crop.height),
			},
			customDimensions,
		);

		// Create URL with query parameters
		const urlBase = asset.url;
		const separator = urlBase.includes('?') ? '&' : '?';
		imageUrl = `${urlBase}${separator}w=${fitDimensions.width}&h=${fitDimensions.height}&fit=max&auto=format`;

		imageWidth = fitDimensions.width;
		imageHeight = fitDimensions.height;
	}

	if (crop?.cropParams) {
		const separator = imageUrl.includes('?') ? '&' : '?';
		const widthParams = imageUrl.includes('w=') ? '' : `&w=${crop.width}&fit=max&auto=format`;
		imageUrl += `${separator}${crop.cropParams}${widthParams}`;
	}

	// Calculate blurDataURL for better user experience
	const separator = imageUrl.includes('?') ? '&' : '?';
	const blurDataURL = `${imageUrl}${separator}blur=20&w=10&q=20`;

	// Set loading behavior based on isHero flag
	const loadingBehavior = loading || (isHero || priority ? 'eager' : 'lazy');

	const hasHotspots = image?.['hotspots'] && image['hotspots'].length > 0;

	return (
		<span className={cn('w-full h-full block relative', className)} {...props}>
			{hasHotspots && <Hotspots hotspots={image['hotspots']} />}
			<Image
				src={imageUrl || ''}
				alt={image.alt || ''}
				style={{
					width: '100%',
					height: '100%',
					objectFit,
					objectPosition, // Apply the hotspot-based positioning
				}}
				width={imageWidth}
				height={imageHeight}
				priority={isHero || priority}
				loading={loadingBehavior}
				sizes={sizes}
				quality={isHero ? Math.min(quality, 90) : quality} // Use lower quality for hero images
				placeholder='blur'
				blurDataURL={blurDataURL}
				fetchPriority={isHero || priority ? 'high' : 'auto'}
			/>
		</span>
	);
}

type Dimensions = {
	width: number;
	height: number;
};

/**
 * Calculates image dimensions that ensure the smallest dimension fits the container
 * while maintaining aspect ratio. The larger dimension may exceed the container.
 *
 * @param originalDimensions The original dimensions of the image
 * @param containerDimensions The dimensions of the container
 * @returns The new dimensions that maintain aspect ratio with smallest dimension fitting
 */
function calculateFitDimensions(originalDimensions: Dimensions, containerDimensions: Dimensions): Dimensions {
	// Calculate the scaling factors for width and height
	const widthScale = containerDimensions.width / originalDimensions.width;
	const heightScale = containerDimensions.height / originalDimensions.height;

	// Choose the larger scale factor to ensure the smallest dimension fits exactly
	// while the larger dimension may exceed the container bounds
	const scale = Math.min(widthScale, heightScale);

	// Calculate the new dimensions
	return {
		width: Math.round(originalDimensions.width * scale),
		height: Math.round(originalDimensions.height * scale),
	};
}

/**
 * Calculates new crop boundaries to fit a target aspect ratio within existing crop boundaries,
 * centered around a hotspot position or image center.
 *
 * @param image - Sanity image object containing crop and hotspot data
 * @param aspectRatio - Target aspect ratio string in format "width:height" or "width/height"
 * @param originalWidth - Original image width in pixels
 * @param originalHeight - Original image height in pixels
 * @returns Object containing crop boundaries (0-1 range) and final cropped dimensions, or original values if aspect ratio invalid
 */
function calculateAspectRatioCrop(
	image: SanityImage,
	aspectRatio: string,
	originalWidth: number,
	originalHeight: number,
): {
	crop: { left: number; top: number; right: number; bottom: number } | undefined;
	width: number;
	height: number;
	cropParams: string | undefined;
} {
	// Validate aspect ratio format
	const aspectMatch = aspectRatio.match(/^(\d+(?:\.\d+)?)[:/](\d+(?:\.\d+)?)$/);
	if (!aspectMatch) {
		// Return original crop and dimensions if aspect ratio format is invalid
		const originalCrop = image.crop;
		const croppedWidth = originalCrop ? (1 - originalCrop.left - originalCrop.right) * originalWidth : originalWidth;
		const croppedHeight = originalCrop ? (1 - originalCrop.top - originalCrop.bottom) * originalHeight : originalHeight;

		return {
			crop: originalCrop,
			width: Math.round(croppedWidth),
			height: Math.round(croppedHeight),
			cropParams: calculateCropParams({ crop: originalCrop, originalWidth, originalHeight }),
		};
	}

	const [, widthRatio, heightRatio] = aspectMatch;
	const targetAspect = Number(widthRatio) / Number(heightRatio);

	// Start with existing crop boundaries or full image if no crop provided
	const cropLeft = image.crop?.left || 0;
	const cropTop = image.crop?.top || 0;
	const cropRight = image.crop?.right || 0;
	const cropBottom = image.crop?.bottom || 0;

	// Calculate current cropped area dimensions
	const currentCroppedWidth = (1 - cropLeft - cropRight) * originalWidth;
	const currentCroppedHeight = (1 - cropTop - cropBottom) * originalHeight;
	const currentAspect = currentCroppedWidth / currentCroppedHeight;

	// Get hotspot position - use center (0.5, 0.5) if no hotspot provided
	const hotspotX = image.hotspot?.x ?? 0.5;
	const hotspotY = image.hotspot?.y ?? 0.5;

	// Convert hotspot to cropped area coordinates
	const hotspotInCropX = (hotspotX - cropLeft) / (1 - cropLeft - cropRight);
	const hotspotInCropY = (hotspotY - cropTop) / (1 - cropTop - cropBottom);

	let newCropLeft = cropLeft;
	let newCropTop = cropTop;
	let newCropRight = cropRight;
	let newCropBottom = cropBottom;

	if (targetAspect > currentAspect) {
		// Need to crop height - make taller crop narrower vertically
		const newCroppedHeight = currentCroppedWidth / targetAspect;
		const heightDiff = currentCroppedHeight - newCroppedHeight;

		// Center around hotspot (or image center if no hotspot)
		const topCrop = heightDiff * hotspotInCropY;
		const bottomCrop = heightDiff * (1 - hotspotInCropY);

		newCropTop = cropTop + topCrop / originalHeight;
		newCropBottom = cropBottom + bottomCrop / originalHeight;
	} else if (targetAspect < currentAspect) {
		// Need to crop width - make wider crop narrower horizontally
		const newCroppedWidth = currentCroppedHeight * targetAspect;
		const widthDiff = currentCroppedWidth - newCroppedWidth;

		// Center around hotspot (or image center if no hotspot)
		const leftCrop = widthDiff * hotspotInCropX;
		const rightCrop = widthDiff * (1 - hotspotInCropX);

		newCropLeft = cropLeft + leftCrop / originalWidth;
		newCropRight = cropRight + rightCrop / originalWidth;
	}

	// Ensure crop values stay within bounds
	const finalCrop = {
		left: Math.max(0, Math.min(1, newCropLeft)),
		top: Math.max(0, Math.min(1, newCropTop)),
		right: Math.max(0, Math.min(1, newCropRight)),
		bottom: Math.max(0, Math.min(1, newCropBottom)),
	};

	// Calculate final cropped dimensions
	const finalCroppedWidth = (1 - finalCrop.left - finalCrop.right) * originalWidth;
	const finalCroppedHeight = (1 - finalCrop.top - finalCrop.bottom) * originalHeight;

	return {
		crop: finalCrop,
		width: Math.round(finalCroppedWidth),
		height: Math.round(finalCroppedHeight),
		cropParams: calculateCropParams({ crop: finalCrop, originalWidth, originalHeight }),
	};
}

function calculateCropParams(props: {
	crop?: { left: number; top: number; right: number; bottom: number };
	originalWidth: number;
	originalHeight: number;
}) {
	if (!props.crop || (props.crop.left === 0 && props.crop.top === 0 && props.crop.right === 0 && props.crop.bottom === 0)) return undefined;

	const cropLeft = Math.round(props.crop.left * props.originalWidth);
	const cropTop = Math.round(props.crop.top * props.originalHeight);
	const cropWidth = Math.round((1 - props.crop.left - props.crop.right) * props.originalWidth);
	const cropHeight = Math.round((1 - props.crop.top - props.crop.bottom) * props.originalHeight);

	return `rect=${cropLeft},${cropTop},${cropWidth},${cropHeight}`;
}

function processImageReference(image: SanityImage) {
	let asset = image.asset;

	if (!asset) {
		return null;
	}

	if (
		'_ref' in asset &&
		typeof asset._ref === 'string' &&
		(!asset.url ||
			!(
				asset.metadata &&
				typeof asset.metadata === 'object' &&
				'dimensions' in asset.metadata &&
				(asset.metadata as { dimensions?: { width: number; height: number } }).dimensions &&
				(asset.metadata as { dimensions: { width: number; height: number } }).dimensions.width &&
				(asset.metadata as { dimensions: { width: number; height: number } }).dimensions.height
			))
	) {
		const projectId = process.env.NEXT_PUBLIC_SANITY_PROJECT_ID;
		const dataset = process.env.NEXT_PUBLIC_SANITY_DATASET;

		if (!projectId || !dataset) {
			return null; // Early return if env vars missing
		}

		const dimensions = getImageDimensions(asset);
		if (!dimensions) {
			return null;
		}
		const { id, width, height, ext } = dimensions;

		const metadata =
			!!width && !!height
				? {
						dimensions: {
							width,
							height,
						},
					}
				: undefined;

		// Build the base URL
		const imageUrl = `https://cdn.sanity.io/images/${projectId}/${dataset}/${id}-${dimensions.width}x${dimensions.height}.${ext}`;

		asset = {
			_ref: image.asset,
			url: imageUrl,
			metadata,
		} as unknown as SanityImage['asset'] & { metadata?: { dimensions?: { width: number; height: number } } };
	}

	return asset as unknown as SanityImage['asset'] & { metadata?: { dimensions?: { width: number; height: number } } };
}

export function getImageDimensions(asset: SanityImage['asset']) {
	let url = asset?._ref;
	if (!url) {
		return null;
	}

	if (url.startsWith('image-')) {
		url = url.replace('image-', '');
	}

	const parts = url.split('-');
	if (parts.length < 3) {
		return null; // Early return for malformed refs
	}

	const [id, dimensions, ext] = parts;
	const dimensionParts = dimensions.split('x');

	if (dimensionParts.length !== 2 || !dimensionParts[0] || !dimensionParts[1]) {
		return null; // Early return for invalid dimensions
	}

	const [widthStr, heightStr] = dimensionParts;

	const width = Number(widthStr) ?? 0;
	const height = Number(heightStr) ?? 0;

	return {
		id,
		width,
		height,
		ext,
	};
}
